'use client'

import React, { useState, useEffect, useRef } from 'react'
import Link from 'next/link'
import Image from 'next/image'
import { useRouter } from 'next/navigation'
import { motion } from 'framer-motion'
import { usePathname } from 'next/navigation'
import styles from './WorkListings.module.scss'
import { Page } from '@/types/database'
import { useAdminContext } from '@/hooks/useAdminContext'
import { usePageNavigation } from '@/hooks/usePageNavigation'
import { usePreloader } from '@/components/PagePreloader/PreloaderContext'
import { sanitizeBasicHtml } from '@/lib/utils/html-sanitizer'


export interface WorkListingsProps {
  title?: string
  displayMode?: 'all' | 'selected' | 'random'
  selectedWorkIds?: string // Comma-separated work IDs
  randomCount?: number
  noTopPadding?: boolean
  noBottomPadding?: boolean
}

interface WorkPage extends Page {
  workData: {
    title: string
    subtitle: string
    thumbnail: string
    date: string
    url: string
  }
}

// Custom hook for individual work item animations - moved outside component
const useWorkItemAnimation = (index: number) => {
  const [shouldAnimate, setShouldAnimate] = useState(false)
  const ref = useRef<HTMLAnchorElement>(null)
  
  const pathname = usePathname()
  const isAdminMode = pathname.startsWith('/admin')
  const { state: preloaderState, isInitialLoad } = usePreloader()
  
  const canAnimate = !isAdminMode && preloaderState === 'hidden' && !isInitialLoad

  useEffect(() => {
    const element = ref.current
    
    if (!element || isAdminMode) {
      if (isAdminMode) setShouldAnimate(true)
      return
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0]
        if (entry.isIntersecting && canAnimate) {
          setShouldAnimate(true)
        }
      },
      { threshold: 0.2 }
    )

    observer.observe(element)
    return () => observer.unobserve(element)
  }, [canAnimate, index, isAdminMode])

  return { ref, shouldAnimate }
}

export const WorkListings: React.FC<WorkListingsProps> = ({
  title = 'Our Works',
  displayMode = 'all',
  selectedWorkIds = '',
  randomCount = 6,
  noTopPadding = false,
  noBottomPadding = false
}) => {
  const [works, setWorks] = useState<WorkPage[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [titleInView, setTitleInView] = useState(false)
  const titleRef = useRef<HTMLHeadingElement>(null)
  const router = useRouter()
  const { isAdminContext } = useAdminContext()
  const pathname = usePathname()
  const isAdminMode = pathname.startsWith('/admin')
  const { navigateToPage } = usePageNavigation()
  const { state: preloaderState, isInitialLoad } = usePreloader()
  
  // Check if animations should be enabled
  const canAnimate = !isAdminMode && preloaderState === 'hidden' && !isInitialLoad

  // Title intersection observer
  useEffect(() => {
    const element = titleRef.current
    
    if (!element || isAdminMode) {
      if (isAdminMode) setTitleInView(true)
      return
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0]
        if (entry.isIntersecting && canAnimate) {
          setTitleInView(true)
        }
      },
      { threshold: 0.3 }
    )

    observer.observe(element)
    return () => observer.unobserve(element)
  }, [canAnimate, isAdminMode])
  
  // Handle work item clicks in admin context
  const handleWorkClick = async (e: React.MouseEvent<HTMLAnchorElement>, work: WorkPage) => {
    if (isAdminContext) {
      e.preventDefault()
      // Redirect to admin editor for this work
      router.push(`/admin/editor/${work.id}`, { scroll: false })
    } else {
      // For front-end navigation, use proper preloading
      const href = work.workData.url
      if (href !== pathname) {
        e.preventDefault()
        await navigateToPage(href, pathname)
      }
    }
  }



  useEffect(() => {
    const fetchWorks = async () => {
      try {
        setLoading(true)
        setError(null)

        // Fetch works from the public works API endpoint
        const response = await fetch('/api/works')
        if (!response.ok) {
          throw new Error('Failed to fetch works')
        }
        
        const workPages: WorkPage[] = await response.json()

        let filteredWorks = workPages

        // Apply display mode filtering
        if (displayMode === 'selected' && selectedWorkIds) {
          const selectedIds = selectedWorkIds.split(',').map(id => id.trim())
          filteredWorks = workPages.filter(work => selectedIds.includes(work.id))
        } else if (displayMode === 'random') {
          // Shuffle and take random count (using deterministic shuffle to avoid hydration issues)
          const shuffled = [...workPages]
          // Use a simple Fisher-Yates shuffle with a seed based on the current date (changes daily)
          const seed = new Date().toDateString()
          let hash = 0
          for (let i = 0; i < seed.length; i++) {
            const char = seed.charCodeAt(i)
            hash = ((hash << 5) - hash) + char
            hash = hash & hash // Convert to 32bit integer
          }
          
          // Use seeded random for consistent server/client rendering within the same day
          let randomSeed = hash
          const seededRandom = () => {
            randomSeed = (randomSeed * 9301 + 49297) % 233280
            return randomSeed / 233280
          }
          
          for (let i = shuffled.length - 1; i > 0; i--) {
            const j = Math.floor(seededRandom() * (i + 1))
            ;[shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]]
          }
          
          filteredWorks = shuffled.slice(0, Math.min(randomCount, shuffled.length))
        }

        setWorks(filteredWorks)
      } catch (err) {

        setError(err instanceof Error ? err.message : 'Failed to load works')
      } finally {
        setLoading(false)
      }
    }

    fetchWorks()
  }, [displayMode, selectedWorkIds, randomCount])

  const getContainerClass = () => {
    const baseClass = styles['work-listings__container']
    if (noTopPadding && noBottomPadding) {
      return `${baseClass} ${styles['work-listings__container--no-vertical']}`
    }
    if (noTopPadding) {
      return `${baseClass} ${styles['work-listings__container--no-top']}`
    }
    if (noBottomPadding) {
      return `${baseClass} ${styles['work-listings__container--no-bottom']}`
    }
    return baseClass
  }

  if (loading) {
    return (
      <div className={styles['work-listings']}>
        <div className={getContainerClass()}>
          <div className={styles['work-listings__loading']}>
            <div className={styles['work-listings__spinner']}></div>
            <p>Loading works...</p>
          </div>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className={styles['work-listings']}>
        <div className={getContainerClass()}>
          <div className={styles['work-listings__error']}>
            <h3>Error loading works</h3>
            <p>{error}</p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className={styles['work-listings']}>
      <div className={getContainerClass()}>
      {/* Header */}
      <motion.h2 
        ref={titleRef}
        className={styles['work-listings__title']}
        initial={{ opacity: isAdminMode ? 1 : 0 }}
        animate={{ opacity: titleInView ? 1 : 0 }}
        transition={{ 
          duration: 0.8,
          ease: [0.25, 0.46, 0.45, 0.94]
        }}
        dangerouslySetInnerHTML={{ __html: sanitizeBasicHtml(title) }}
      />

      {/* Works Grid */}
      {works.length === 0 ? (
        <div className={styles['work-listings__empty']}>
          <h3>No works found</h3>
          <p>
            {displayMode === 'selected' 
              ? 'No works selected or selected works not found.' 
              : 'No works have been created yet.'}
          </p>
        </div>
      ) : (
        <div className={styles['work-listings__grid']}>
          {works.map((work, index) => {
            const WorkItem = () => {
              const { ref, shouldAnimate } = useWorkItemAnimation(index)
              
              return (
                <Link 
                  ref={ref}
                  href={work.workData.url}
                  className={styles['work-listings__card']}
                  onClick={(e) => handleWorkClick(e, work)}
                >
                    <motion.div 
                      className={styles['work-listings__card-image']}
                      initial={{ opacity: isAdminMode ? 1 : 0 }}
                      animate={{ opacity: shouldAnimate ? 1 : 0 }}
                      transition={{ 
                        duration: 0.8,
                        delay: 0, // Image appears first
                        ease: [0.25, 0.46, 0.45, 0.94]
                      }}
                    >
                      <Image 
                        src={work.workData.thumbnail} 
                        alt={work.workData.title}
                        className={styles['work-listings__card-img']}
                        width={1200}
                        quality={95}
                        height={675}
                        sizes="(max-width: 640px) 100vw, (max-width: 1024px) 100vw, 1200px"
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      />
                    </motion.div>
                    
                    <motion.div 
                      className={styles['work-listings__card-content']}
                      initial={{ opacity: isAdminMode ? 1 : 0 }}
                      animate={{ opacity: shouldAnimate ? 1 : 0 }}
                      transition={{ 
                        duration: 0.8,
                        delay: 0.6, // Content appears slightly after image
                        ease: [0.25, 0.46, 0.45, 0.94]
                      }}
                    >
                      <h3 
                        className={styles['work-listings__card-title']}
                        dangerouslySetInnerHTML={{ __html: sanitizeBasicHtml(work.workData.title) }}
                      />
                      
                      <div className={styles['work-listings__card-meta']}>
                        {work.workData.subtitle && (
                          <span className={styles['work-listings__card-subtitle']}>
                            {work.workData.subtitle}
                          </span>
                        )}
                      </div>
                    </motion.div>
                  </Link>
              )
            }
            
            return <WorkItem key={work.id} />
          })}
        </div>
      )}
      </div>
    </div>
  )
}